import React, { useState } from "react";
import ChartBlock from "./ChartBlock";
import ClarificationBlock from "./ClarificationBlock";
import ExportButton from "./ExportButton";
import Markdown from "./Markdown";
import SiraguLogo from "./SiraguLogo";
import "./MessageBubble.css";

/* ── Avatar palette ─────────────────────────────────────────────────────── */
const AVATAR_PALETTE = [
  ["#6C63FF", "#8A7CFF"],
  ["#F59E0B", "#FBBF24"],
  ["#10B981", "#34D399"],
  ["#3B82F6", "#60A5FA"],
  ["#EF4444", "#F87171"],
  ["#8B5CF6", "#A78BFA"],
  ["#EC4899", "#F472B6"],
  ["#14B8A6", "#2DD4BF"],
];

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

/* ── Person-data detector ───────────────────────────────────────────────── */
function isPersonLike(columns) {
  if (!columns || columns.length === 0) return false;
  const lc = columns.map((c) => c.toLowerCase());
  return lc.some(
    (c) =>
      c === "first_name" ||
      c === "last_name" ||
      c === "full_name" ||
      c === "name" ||
      c === "student_name" ||
      c === "applicant_name" ||
      c === "lead_name"
  );
}

/* ── Extraction helpers ─────────────────────────────────────────────────── */
function extractName(row, columns) {
  const lc = columns.map((c) => c.toLowerCase());
  const fnIdx = lc.findIndex((c) => c === "first_name");
  const lnIdx = lc.findIndex((c) => c === "last_name");
  if (fnIdx !== -1 && lnIdx !== -1) {
    const fn = row[columns[fnIdx]] || "";
    const ln = row[columns[lnIdx]] || "";
    return `${fn} ${ln}`.trim() || null;
  }
  const candidates = ["full_name", "name", "student_name", "applicant_name", "lead_name"];
  for (const c of candidates) {
    const idx = lc.findIndex((col) => col === c);
    if (idx !== -1 && row[columns[idx]]) return String(row[columns[idx]]);
  }
  return null;
}

function extractInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] || "").toUpperCase() +
    (parts[1]?.[0] || "").toUpperCase()
  );
}

// Find a real photo URL in the row (photo_url / photo_path / image_url / avatar ...), so the
// person card shows the actual picture instead of initials. Accepts any http(s) value on an
// image-named column; the <img> onError still falls back to initials if the link is dead.
function extractPhoto(row, columns) {
  for (let i = 0; i < columns.length; i++) {
    if (!IMG_COL_RE.test((columns[i] || "").toLowerCase())) continue;
    const v = row[columns[i]];
    if (v == null) continue;
    const s = String(v).trim();
    if (/^https?:\/\//i.test(s)) return s;
  }
  return null;
}

function extractSubtitle(row, columns) {
  const lc = columns.map((c) => c.toLowerCase());
  const priority = ["course_name", "course", "program", "department", "dept", "status", "email"];
  for (const p of priority) {
    const idx = lc.findIndex((c) => c.includes(p));
    if (idx !== -1 && row[columns[idx]]) return String(row[columns[idx]]);
  }
  return null;
}

function extractMetric(row, columns, displayColumns) {
  const lc = columns.map((c) => c.toLowerCase());
  const priority = ["score", "total_score", "lead_score", "percentage", "rank", "marks", "amount"];
  for (const p of priority) {
    const idx = lc.findIndex((c) => c.includes(p));
    if (idx !== -1 && row[columns[idx]] !== null && row[columns[idx]] !== undefined) {
      const label = (displayColumns && displayColumns[idx]) || columns[idx];
      const val = row[columns[idx]];
      return { label, value: typeof val === "number" ? val.toLocaleString() : String(val) };
    }
  }
  return null;
}

/* ── Date/event extraction ──────────────────────────────────────────────── */
function extractEvents(row, columns) {
  const lc = columns.map((c) => c.toLowerCase());
  const events = [];

  // --- Preferred: pre-formatted columns from single-SELECT SQL ---
  const bdIdx   = lc.findIndex((c) => c === "birthday");
  const annIdx  = lc.findIndex((c) => c === "work_anniversary");
  const btmIdx  = lc.findIndex((c) => c === "birthday_this_month");
  const atmIdx  = lc.findIndex((c) => c === "anniversary_this_month");

  const hasBirthdayFlag     = btmIdx !== -1;
  const hasAnniversaryFlag  = atmIdx !== -1;

  if (bdIdx !== -1 && row[columns[bdIdx]]) {
    // Show birthday date only when birthday_this_month flag is truthy (or flag column absent)
    if (!hasBirthdayFlag || row[columns[btmIdx]]) {
      events.push({ text: String(row[columns[bdIdx]]), type: "birthday", icon: "🎂" });
    }
  }

  if (annIdx !== -1 && row[columns[annIdx]]) {
    // Show anniversary date only when anniversary_this_month flag is truthy (or flag column absent)
    if (!hasAnniversaryFlag || row[columns[atmIdx]]) {
      events.push({ text: String(row[columns[annIdx]]), type: "anniversary", icon: "🎉" });
    }
  }

  // --- Fallback: raw birthday_month / birthday_day numbers (UNION SQL) ---
  if (events.length === 0) {
    const monthIdx = lc.findIndex((c) => c === "birthday_month");
    const dayIdx   = lc.findIndex((c) => c === "birthday_day");
    if (monthIdx !== -1 && dayIdx !== -1) {
      const m = parseInt(row[columns[monthIdx]], 10);
      const d = parseInt(row[columns[dayIdx]], 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        events.push({ text: `${MONTH_NAMES[m - 1]} ${d}`, type: "unknown", icon: "📅" });
      }
    }
  }

  return events.length > 0 ? events : null;
}

/* ── Per-row action filtering ───────────────────────────────────────────── */
function getApplicableActions(row, columns, actions) {
  if (!actions || actions.length === 0) return [];
  const lc = columns.map((c) => c.toLowerCase());

  return actions.filter((action) => {
    // Normalise field names (REST uses snake_case, GraphQL uses camelCase)
    const targetField = (action.target_field || action.targetField || "user_id").toLowerCase();
    const milestoneType = (action.milestone_type || action.milestoneType || "").toLowerCase();

    // Must have a non-null target id
    const targetIdx = lc.findIndex((c) => c === targetField);
    if (targetIdx === -1 || !row[columns[targetIdx]]) return false;

    // Per-row event filtering using flag columns when available
    if (milestoneType === "birthday") {
      const btmIdx = lc.findIndex((c) => c === "birthday_this_month");
      if (btmIdx !== -1) return !!row[columns[btmIdx]];
    }
    if (milestoneType === "work_anniversary") {
      const atmIdx = lc.findIndex((c) => c === "anniversary_this_month");
      if (atmIdx !== -1) return !!row[columns[atmIdx]];
    }

    // No flag columns (UNION fallback) — show all actions for rows with valid target
    return true;
  });
}

/* ── PersonCard ─────────────────────────────────────────────────────────── */
function PersonCard({ row, columns, displayColumns, index, actions, onAction }) {
  const name     = extractName(row, columns) || "Unknown";
  const initials = extractInitials(name);
  const subtitle = extractSubtitle(row, columns);
  const metric   = extractMetric(row, columns, displayColumns);
  const events   = extractEvents(row, columns);
  const applicable = getApplicableActions(row, columns, actions || []);
  const [from, to] = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
  const photo    = extractPhoto(row, columns);
  const [imgOk, setImgOk] = useState(true);
  const showPhoto = photo && imgOk;

  return (
    <div className="person-card">
      <div className="person-rank">#{index + 1}</div>
      <div
        className="person-avatar"
        style={showPhoto ? undefined : { background: `linear-gradient(135deg, ${from}, ${to})` }}
      >
        {showPhoto ? (
          <img
            className="person-avatar-img"
            src={photo}
            alt={name}
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          initials
        )}
      </div>
      <div className="person-info">
        <div className="person-name">{name}</div>
        {subtitle && <div className="person-subtitle">{subtitle}</div>}

        {events && (
          <div className="person-events">
            {events.map((ev, i) => (
              <span key={i} className={`person-event-badge person-event-${ev.type}`}>
                {ev.icon} {ev.text}
              </span>
            ))}
          </div>
        )}

        {metric && (
          <div className="person-metric">
            <span className="metric-value">{metric.value}</span>
            <span className="metric-label">{metric.label}</span>
          </div>
        )}

        {applicable.length > 0 && (
          <div className="person-actions">
            {applicable.map((action, i) => (
              <button
                key={i}
                className={`person-action-btn action-${(action.milestone_type || action.milestoneType || "default").replace("_", "-")}`}
                onClick={() => onAction && onAction(action, row)}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── PersonCardGrid ─────────────────────────────────────────────────────── */
function PersonCardGrid({ columns, displayColumns, rows, rowCount, actions, onAction }) {
  const [showAll, setShowAll] = useState(false);
  const LIMIT = 8;
  const visible = showAll ? rows : rows.slice(0, LIMIT);
  const hidden = rows.length - LIMIT;

  return (
    <div className="person-card-section">
      <div className="result-meta-bar">
        <span className="result-count-badge">{rowCount} found</span>
      </div>
      <div className="person-card-grid">
        {visible.map((row, i) => (
          <PersonCard
            key={i}
            row={row}
            columns={columns}
            displayColumns={displayColumns}
            index={i}
            actions={actions}
            onAction={onAction}
          />
        ))}
      </div>
      {!showAll && hidden > 0 && (
        <button className="show-more-btn" onClick={() => setShowAll(true)}>
          Show {hidden} more result{hidden !== 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

/* ── SmartResultsRenderer ───────────────────────────────────────────────── */
function SmartResultsRenderer({ columns, displayColumns, rows, rowCount, actions, onAction }) {
  if (!columns || !rows || rows.length === 0) return null;

  if (isPersonLike(columns)) {
    return (
      <PersonCardGrid
        columns={columns}
        displayColumns={displayColumns}
        rows={rows}
        rowCount={rowCount}
        actions={actions}
        onAction={onAction}
      />
    );
  }

  return (
    <ResultsTable
      columns={columns}
      displayColumns={displayColumns}
      rows={rows}
      rowCount={rowCount}
    />
  );
}

/* ── MessageBubble ──────────────────────────────────────────────────────── */
function MessageBubble({ message, onSend, onFeedback }) {
  const {
    role, content, sql, columns, rows, rowCount, error,
    intent, renderAs, chart, messageProp, suggestions,
    actions,
    export: isExport,
  } = { ...message, messageProp: message.message };

  const displayColumns = message.displayColumns || columns || [];
  const isUser = role === "user";
  const hasData = columns && columns.length > 0 && rows && rows.length > 0;
  const showChart = renderAs === "chart" && chart && hasData;
  // "text" means words-only (e.g. the caller explicitly asked for no chart/table) — the
  // table must not render alongside it just because rows/columns are still present in the
  // payload (kept there for CSV export and message-building, not for display).
  const showTable = renderAs !== "text" && hasData && (!showChart || renderAs !== "chart");
  const showClarification =
    renderAs === "text" && (messageProp || (suggestions && suggestions.length > 0));

  return (
    <div className={`message ${isUser ? "message-user" : "message-assistant"}`}>
      <div className={`message-icon ${isUser ? "icon-user" : "icon-ai"}`}>
        {isUser ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          </svg>
        ) : (
          <SiraguLogo size={22} className="message-logo" />
        )}
      </div>

      <div className="message-body">
        <div className="message-role">
          <span>{isUser ? "You" : "Siragu"}</span>
          {!isUser && (
            <SpeakButton
              text={
                messageProp ||
                content ||
                (rowCount
                  ? `I found ${rowCount} result${rowCount !== 1 ? "s" : ""}. The details are shown below.`
                  : "")
              }
            />
          )}
        </div>

        {showClarification && (
          <ClarificationBlock message={messageProp} suggestions={suggestions} onSend={onSend} />
        )}

        {content && !showClarification && (
          <div className={`message-text ${error ? "message-error" : ""}`}>
            {isUser ? (
              content.split("\n").map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))
            ) : (
              <Markdown text={content} />
            )}
          </div>
        )}

        {sql && <SQLBlock sql={sql} />}

        {showChart && <ChartBlock chart={chart} columns={columns} rows={rows} />}

        {showChart && hasData && (
          <CollapsibleTable columns={columns} displayColumns={displayColumns} rows={rows} rowCount={rowCount} />
        )}

        {showTable && !showChart && (
          <SmartResultsRenderer
            columns={columns}
            displayColumns={displayColumns}
            rows={rows}
            rowCount={rowCount}
            actions={actions || []}
            onAction={null}
          />
        )}

        {!showClarification && messageProp && hasData && messageProp !== content && (
          <div className="partial-answer-note">
            <span className="partial-note-icon">&#9432;</span> {messageProp}
          </div>
        )}

        {hasData && (
          <ExportButton
            columns={columns}
            rows={rows}
            displayColumns={displayColumns}
            summary={content || messageProp}
            chartType={chart?.type}
          />
        )}

        {!showClarification && suggestions && suggestions.length > 0 && (
          <div className="followup-suggestions">
            <div className="followup-label">Related questions</div>
            <div className="followup-chips">
              {suggestions.map((s, i) => (
                <button key={i} className="followup-chip" onClick={() => onSend && onSend(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isUser && !error && (
          <FeedbackBar
            feedback={message.feedback}
            onFeedback={(rating, comment) =>
              onFeedback && onFeedback(message.localId, rating, comment)
            }
          />
        )}
      </div>
    </div>
  );
}

/* ── FeedbackBar (thumbs up / down for assistant replies) ────────────────── */
function FeedbackBar({ feedback, onFeedback }) {
  const [thanks, setThanks] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");

  const flashThanks = () => {
    setThanks(true);
    setTimeout(() => setThanks(false), 2400);
  };

  const handleUp = () => {
    setShowReason(false);
    onFeedback && onFeedback("up");
    flashThanks();
  };

  const handleDown = () => {
    // Reveal the inline reason box instead of firing immediately.
    setThanks(false);
    setShowReason(true);
  };

  const submitReason = () => {
    onFeedback && onFeedback("down", reason.trim() || undefined);
    setShowReason(false);
    setReason("");
    flashThanks();
  };

  return (
    <div className="feedback-bar-wrap">
      <div className="feedback-bar">
        <span className="feedback-label">Was this helpful?</span>
        <button
          type="button"
          className={`feedback-btn${feedback === "up" ? " active up" : ""}`}
          onClick={handleUp}
          aria-label="Helpful"
          title="Helpful"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M7 10v11M14 4l-1.5 6H21a1 1 0 0 1 1 1.2l-1.7 7A2 2 0 0 1 18.3 21H7V10l4-7a2 2 0 0 1 2 1v0z"
              stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          type="button"
          className={`feedback-btn${feedback === "down" ? " active down" : ""}`}
          onClick={handleDown}
          aria-label="Not helpful"
          title="Not helpful"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M17 14V3M10 20l1.5-6H3a1 1 0 0 1-1-1.2l1.7-7A2 2 0 0 1 5.7 3H17v11l-4 7a2 2 0 0 1-2-1v0z"
              stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>
        {thanks && <span className="feedback-thanks">Thanks for the feedback!</span>}
      </div>

      {showReason && (
        <div className="dislike-reason">
          <textarea
            className="dislike-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What went wrong? (optional)"
            rows={2}
            autoFocus
          />
          <div className="dislike-reason-actions">
            <button
              type="button"
              className="dislike-reason-submit"
              onClick={submitReason}
            >
              Submit
            </button>
            <button
              type="button"
              className="dislike-reason-cancel"
              onClick={() => { setShowReason(false); setReason(""); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SpeakButton (text-to-speech for replies) ───────────────────────────── */
const TTS_SUPPORTED =
  typeof window !== "undefined" && "speechSynthesis" in window;

function cleanForSpeech(text) {
  return String(text)
    .replace(/[*_#>`|]/g, " ")      // strip markdown symbols
    .replace(/\s+/g, " ")
    .trim();
}

// Voices load asynchronously — warm them up so the picker has data on first click.
if (TTS_SUPPORTED) {
  try {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
  } catch (e) {
    /* ignore */
  }
}

/* Prefer an Indian-English female voice; fall back gracefully. */
function pickIndianFemaleVoice() {
  const voices = (TTS_SUPPORTED && window.speechSynthesis.getVoices()) || [];
  if (!voices.length) return null;
  const femaleHints = [
    "female", "heera", "kalpana", "priya", "veena", "aditi", "raveena", "swara",
    "neerja", "isha", "ananya", "google हिन्दी", "google hindi",
  ];
  const isFemale = (v) => femaleHints.some((h) => v.name.toLowerCase().includes(h));
  const enIN = voices.filter((v) => /en[-_]?IN/i.test(v.lang));
  const hiIN = voices.filter((v) => /hi[-_]?IN/i.test(v.lang));
  return (
    enIN.find(isFemale) ||
    enIN[0] ||
    hiIN.find(isFemale) ||
    hiIN[0] ||
    voices.find((v) => /^en/i.test(v.lang) && isFemale(v)) ||
    null
  );
}

function SpeakButton({ text }) {
  const [speaking, setSpeaking] = useState(false);
  const clean = text ? cleanForSpeech(text) : "";
  if (!TTS_SUPPORTED || !clean) return null;

  const toggle = () => {
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    const v = pickIndianFemaleVoice();
    if (v) {
      u.voice = v;
      u.lang = v.lang;
    } else {
      u.lang = "en-IN"; // hint Indian English even without a named voice
    }
    u.rate = 0.88;    // slower — calm, easy-to-follow pace
    u.pitch = 1.05;   // gently higher — soft, natural female tone
    u.volume = 0.9;   // softer, not harsh
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
  };

  return (
    <button
      type="button"
      className={`speak-btn${speaking ? " speaking" : ""}`}
      onClick={toggle}
      aria-label={speaking ? "Stop reading" : "Read reply aloud"}
      title={speaking ? "Stop reading" : "Read aloud"}
    >
      {speaking ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M11 5L6 9H3v6h3l5 4V5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M15.5 8.5a4 4 0 0 1 0 7M18 6a7 7 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      <span className="speak-btn-label">{speaking ? "Stop" : "Read aloud"}</span>
    </button>
  );
}

/* ── SQLBlock ───────────────────────────────────────────────────────────── */
function SQLBlock({ sql }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(sql)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => {
        const ta = document.createElement("textarea");
        ta.value = sql;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  return (
    <div className="sql-block">
      <div className="sql-header">
        <span>SQL Query</span>
        <button className="copy-btn" onClick={handleCopy}>{copied ? "Copied!" : "Copy"}</button>
      </div>
      <pre className="sql-code">{sql}</pre>
    </div>
  );
}

/* ── ResultsTable ───────────────────────────────────────────────────────── */
function ResultsTable({ columns, displayColumns, rows, rowCount }) {
  const headers =
    displayColumns && displayColumns.length === columns.length ? displayColumns : columns;
  return (
    <div className="results-container">
      <div className="results-header">
        <span className="result-count-badge">{rowCount} row{rowCount !== 1 ? "s" : ""}</span>
      </div>
      <div className="table-scroll">
        <table className="results-table">
          <thead>
            <tr>{headers.map((h, idx) => <th key={columns[idx]}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => <td key={col}>{renderCell(col, row[col])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── CollapsibleTable ───────────────────────────────────────────────────── */
function CollapsibleTable({ columns, displayColumns, rows, rowCount }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="collapsible-table">
      <button className="collapsible-toggle" onClick={() => setOpen(!open)}>
        {open ? "Hide" : "Show"} data table
        <span className="toggle-count"> ({rowCount} row{rowCount !== 1 ? "s" : ""})</span>
      </button>
      {open && (
        <ResultsTable columns={columns} displayColumns={displayColumns} rows={rows} rowCount={rowCount} />
      )}
    </div>
  );
}

const IMG_URL_RE = /^https?:\/\/[^\s]+\.(jpe?g|png|gif|webp|svg|bmp)(\?[^\s]*)?$/i;
const IMG_COL_RE = /(photo|image|avatar|picture|logo|thumbnail|img|pic|banner)/i;
const URL_RE = /^https?:\/\/\S+$/i;

function renderCell(col, value) {
  if (value === null || value === undefined) return <span className="cell-null">—</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "object") return JSON.stringify(value);
  const str = String(value);
  // Render photos/images inline as thumbnails.
  if (IMG_URL_RE.test(str) || (IMG_COL_RE.test(col || "") && URL_RE.test(str))) {
    return (
      <img
        className="cell-image"
        src={str}
        alt={col || "image"}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  // Other URLs → clickable links.
  if (URL_RE.test(str)) {
    return (
      <a href={str} target="_blank" rel="noreferrer" className="cell-link">
        {str.length > 44 ? str.slice(0, 44) + "…" : str}
      </a>
    );
  }
  return str;
}

export default MessageBubble;
